import { useEffect, useState } from "react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DollarSign, Plus, Edit, Trash2, FileText, Send, CheckCircle2, Download } from "lucide-react";
import { StatusBadge, formatDate } from "@/lib/ui";
import { Link } from "react-router-dom";

const STATUSES = ["draft", "sent", "paid", "unpaid", "overdue"];

export default function BillingTab({ clientId, isReadOnly, onChange }) {
  const { currentOrgId, user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      service: "",
      service_date: new Date().toISOString().slice(0, 10),
      hours: "1",
      rate: "0",
      staff_user_id: user?.id || "",
      status: "draft",
      notes: "",
    };
  }

  const load = async () => {
    setLoading(true);
    const [e, s, inv] = await Promise.all([
      supabase.from("billing_entries").select("*, profiles:staff_user_id(full_name)")
        .eq("client_id", clientId).order("service_date", { ascending: false }),
      supabase.from("organization_members").select("user_id, profiles!inner(id, full_name, email)")
        .eq("organization_id", currentOrgId).eq("status", "active"),
      supabase.from("invoices").select("*").eq("client_id", clientId).order("created_at", { ascending: false }),
    ]);
    setEntries(e.data || []);
    setStaff(s.data || []);
    setInvoices(inv.data || []);
    setLoading(false);
  };

  useEffect(() => { if (clientId && currentOrgId) load(); /* eslint-disable-next-line */ }, [clientId, currentOrgId]);

  const openNew = () => { setEditing(null); setForm(blank()); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      service: b.service, service_date: b.service_date,
      hours: String(b.hours || 0), rate: String(b.rate || 0),
      staff_user_id: b.staff_user_id || "", status: b.status, notes: b.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.service.trim()) return toast.error("Service is required");
    const payload = {
      ...form,
      hours: Number(form.hours) || 0,
      rate: Number(form.rate) || 0,
      staff_user_id: form.staff_user_id || null,
    };
    try {
      if (editing) {
        await apiFetch(`/billing-entries/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        toast.success("Billing entry updated");
      } else {
        await apiFetch("/billing-entries", { method: "POST",
          body: JSON.stringify({ ...payload, organization_id: currentOrgId, client_id: clientId }) });
        toast.success("Billing entry created");
      }
      setOpen(false); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete this billing entry for "${b.service}"?`)) return;
    try {
      await apiFetch(`/billing-entries/${b.id}`, { method: "DELETE" });
      toast.success("Deleted"); load(); onChange && onChange();
    } catch (e) { toast.error(e.message); }
  };

  const total = entries.reduce((s, e) => s + (Number(e.total) || 0), 0);
  const paid = entries.filter((e) => e.status === "paid").reduce((s, e) => s + (Number(e.total) || 0), 0);
  const outstanding = entries.filter((e) => ["sent", "unpaid", "overdue"].includes(e.status))
    .reduce((s, e) => s + (Number(e.total) || 0), 0);

  const invTotal = invoices.reduce((s, i) => s + (Number(i.total_cents) || 0), 0) / 100;
  const invPaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (Number(i.total_cents) || 0), 0) / 100;
  const invOutstanding = invoices.filter((i) => ["sent", "overdue"].includes(i.status))
    .reduce((s, i) => s + (Number(i.total_cents) || 0), 0) / 100;

  const sendInvoice = async (id) => {
    try {
      const r = await apiFetch(`/invoices/${id}/send`, { method: "POST", body: JSON.stringify({ send_email: true }) });
      if (r.email_sent) toast.success("Sent — client emailed");
      else if (r.portal_link) {
        try { await navigator.clipboard.writeText(r.portal_link); toast.success("Pay link copied to clipboard"); }
        catch { toast.success("Invoice marked sent"); }
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const markPaid = async (id, num) => {
    if (!window.confirm(`Mark ${num} as paid?`)) return;
    try { await apiFetch(`/invoices/${id}/mark-paid`, { method: "POST", body: "{}" }); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const downloadInvoicePDF = async (id, num) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${id}/pdf`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); link.download = `${num}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error("PDF failed"); }
  };

  const liveTotal = ((Number(form.hours) || 0) * (Number(form.rate) || 0)).toFixed(2);

  if (loading) return <div className="text-center py-12 text-sm text-[#57534E]">Loading billing…</div>;

  return (
    <div className="space-y-4" data-testid="billing-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg tracking-tight">Client billing</h3>
          <p className="text-xs text-[#57534E]">Service &amp; billing entries for this client.</p>
        </div>
        {!isReadOnly && (
          <Button data-testid="new-billing-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">
            <Plus size={14} className="mr-1.5" /> New billing entry
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetaCard label="Total billed" value={`$${total.toFixed(2)}`} />
        <MetaCard label="Paid" value={`$${paid.toFixed(2)}`} />
        <MetaCard label="Outstanding" value={`$${outstanding.toFixed(2)}`} />
      </div>

      {/* Invoices section */}
      <div className="pt-4 border-t border-[#E8E2D9]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-semibold text-lg tracking-tight flex items-center gap-2">
              <FileText size={16} className="text-[#E8712C]" strokeWidth={1.75} /> Invoices
            </h3>
            <p className="text-xs text-[#57534E]">Formal invoices this client has received.</p>
          </div>
          {!isReadOnly && (
            <Link to="/app/invoices">
              <Button variant="outline" className="rounded-full" data-testid="new-invoice-link">
                <Plus size={13} className="mr-1.5" /> New invoice
              </Button>
            </Link>
          )}
        </div>

        {invoices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <MetaCard label="Invoiced" value={`$${invTotal.toFixed(2)}`} />
            <MetaCard label="Paid" value={`$${invPaid.toFixed(2)}`} />
            <MetaCard label="Outstanding" value={`$${invOutstanding.toFixed(2)}`} />
          </div>
        )}

        {invoices.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-[#E8E2D9]">
            <FileText size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
            <p className="text-sm">No invoices yet for this client.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
                <tr>
                  <th className="text-left px-5 py-3">Number</th>
                  <th className="text-left px-5 py-3">Issued</th>
                  <th className="text-left px-5 py-3">Due</th>
                  <th className="text-right px-5 py-3">Amount</th>
                  <th className="text-left px-5 py-3">Status</th>
                  {!isReadOnly && <th />}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E2D9]">
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`client-inv-${inv.id}`}>
                    <td className="px-5 py-3 font-medium">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-[#57534E]">{inv.issued_at ? formatDate(inv.issued_at) : "—"}</td>
                    <td className="px-5 py-3 text-[#57534E]">{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                    <td className="px-5 py-3 text-right font-semibold">
                      ${(inv.total_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-xs text-[#A8A29E]">{inv.currency.toUpperCase()}</span>
                    </td>
                    <td className="px-5 py-3"><StatusBadge value={inv.status} /></td>
                    {!isReadOnly && (
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {inv.status === "draft" && (
                          <Button size="sm" variant="ghost" onClick={() => sendInvoice(inv.id)} data-testid={`inv-send-${inv.id}`} title="Send"><Send size={13} /></Button>
                        )}
                        {inv.status === "sent" && (
                          <Button size="sm" variant="ghost" onClick={() => markPaid(inv.id, inv.invoice_number)} data-testid={`inv-mp-${inv.id}`} title="Mark paid" className="text-[#1F6B36]"><CheckCircle2 size={13} /></Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => downloadInvoicePDF(inv.id, inv.invoice_number)} data-testid={`inv-pdf-${inv.id}`} title="Download PDF"><Download size={13} /></Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legacy billing entries */}
      <div className="pt-4 border-t border-[#E8E2D9]">
        <h3 className="font-display font-semibold text-lg tracking-tight mb-3">Time &amp; billing entries</h3>

      {entries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E8E2D9]">
          <DollarSign size={22} className="mx-auto text-[#A8A29E] mb-2" strokeWidth={1.5} />
          <p className="text-sm font-medium">No billing entries yet</p>
          <p className="text-xs text-[#57534E] mt-1">Create the first entry for services delivered to this client.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Service</th>
                <th className="text-left px-5 py-3">Staff</th>
                <th className="text-right px-5 py-3">Hours</th>
                <th className="text-right px-5 py-3">Rate</th>
                <th className="text-right px-5 py-3">Total</th>
                <th className="text-left px-5 py-3">Status</th>
                {!isReadOnly && <th />}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {entries.map((b) => (
                <tr key={b.id} data-testid={`bill-row-${b.id}`}>
                  <td className="px-5 py-3">{formatDate(b.service_date)}</td>
                  <td className="px-5 py-3 font-medium">{b.service}</td>
                  <td className="px-5 py-3 text-[#57534E]">{b.profiles?.full_name || "—"}</td>
                  <td className="px-5 py-3 text-right">{Number(b.hours).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right">${Number(b.rate).toFixed(2)}</td>
                  <td className="px-5 py-3 text-right font-semibold">${Number(b.total).toFixed(2)}</td>
                  <td className="px-5 py-3"><StatusBadge value={b.status} /></td>
                  {!isReadOnly && (
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`edit-bill-${b.id}`} onClick={() => openEdit(b)}><Edit size={13} /></Button>
                      <Button size="sm" variant="ghost" data-testid={`del-bill-${b.id}`} onClick={() => remove(b)} className="text-red-600"><Trash2 size={13} /></Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit billing entry" : "New billing entry"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Service *</Label>
              <Input value={form.service} onChange={(e) => setForm({...form, service: e.target.value})} className="mt-1.5" placeholder="e.g. Care coordination call" data-testid="bill-service" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label>
                <Input type="date" value={form.service_date} onChange={(e) => setForm({...form, service_date: e.target.value})} className="mt-1.5" data-testid="bill-date" />
              </div>
              <div><Label>Staff member</Label>
                <Select value={form.staff_user_id} onValueChange={(v) => setForm({...form, staff_user_id: v})}>
                  <SelectTrigger className="mt-1.5" data-testid="bill-staff"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.profiles.full_name || s.profiles.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Hours / quantity</Label>
                <Input type="number" step="0.01" value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} className="mt-1.5" data-testid="bill-hours" />
              </div>
              <div><Label>Rate ($)</Label>
                <Input type="number" step="0.01" value={form.rate} onChange={(e) => setForm({...form, rate: e.target.value})} className="mt-1.5" data-testid="bill-rate" />
              </div>
            </div>
            <div className="bg-[#FAF8F5] rounded-lg p-3 flex items-center justify-between">
              <span className="text-sm text-[#57534E]">Total</span>
              <span className="text-lg font-display font-bold">${liveTotal}</span>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1.5" data-testid="bill-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button onClick={() => setOpen(false)} variant="outline" className="rounded-full">Cancel</Button>
            <Button data-testid="save-billing-btn" onClick={save} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

function MetaCard({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-4">
      <div className="text-xs text-[#A8A29E] uppercase tracking-widest">{label}</div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
    </div>
  );
}
